import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { XMarkIcon } from "@heroicons/react/24/outline";
import React from "react";

/**
 * ProductSelectorDialog - Optimized dialog for ModernProductSelectorV2
 * 
 * Features:
 * - Minimal padding/margin for better mobile experience
 * - Full-height on mobile, constrained on desktop
 * - Proper flexbox structure for scrolling
 */
const ProductSelectorDialog = (props) => {
  const {
    title,
    children,
    onClose = () => {},
    open = false,
    size = "7xl",
    closeOnOutsideClick = true,
    zIndex = 3000,
  } = props;

  const { t } = useTranslation();

  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative"
          style={{ zIndex: zIndex ?? 3000 }}
          onClose={closeOnOutsideClick ? onClose : () => {}}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-hidden">
            <div className="flex min-h-full items-stretch justify-center p-0 sm:p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={`
                    ${sizeClasses[size]}
                    w-full transform bg-white text-left align-middle shadow-xl transition-all
                    h-screen max-h-screen rounded-none sm:rounded-lg sm:h-auto sm:max-h-[85vh]
                    flex flex-col overflow-hidden
                    p-0 sm:p-4
                  `}
                >
                  {title && (
                    <div className="flex-shrink-0 px-4 pt-4 pb-2 sm:px-0 sm:pt-0 sm:pb-0 border-b border-gray-200 sm:border-b-0">
                      <Dialog.Title
                        as="h3"
                        className="flex justify-between items-center text-lg font-medium leading-6 text-gray-900"
                      >
                        <div>{t(title)}</div>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent px-2 py-2 bg-white text-sm font-medium text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          onClick={onClose}
                        >
                          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </Dialog.Title>
                    </div>
                  )}
                  <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                    {children}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

ProductSelectorDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node,
  title: PropTypes.string,
  size: PropTypes.oneOf([
    "sm",
    "md",
    "lg",
    "xl",
    "2xl",
    "3xl",
    "4xl",
    "5xl",
    "6xl",
    "7xl",
  ]),
  zIndex: PropTypes.number,
  closeOnOutsideClick: PropTypes.bool,
};

export default ProductSelectorDialog;

